import Analytics from 'helpers/analytics';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { useRef, useState, useEffect, useCallback } from 'react';
import { loggerInfo } from 'utils/infoLog';
import { postReferEmployee } from '../services/collaborators';
import { useToast } from '@welbe/web-design-system';

interface SuccessModalHook {
	onCloseReferFlow: () => void;
	onClearSelectedEmployees: () => void;
	onRetryTable: () => void;
}

export const useSuccessModal = ({
	onCloseReferFlow,
	onClearSelectedEmployees,
	onRetryTable,
}: SuccessModalHook) => {
	const timeOutSuccess = 3000;
	const [isOpenSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
	const [referedAmount, setReferedAmout] = useState<any>({
		success: 0,
		alreadyReferred: 0,
		error: 0,
	});

	const onClearReferedAmount = () =>
		setReferedAmout({
			success: 0,
			alreadyReferred: 0,
			error: 0,
		});

	const successModalTimeout = useRef<any>(null);

	const onSaveResults = (data: any) => {
		setReferedAmout(data);
		onClearReferedAmount();
	};

	const handleOpenModalSuccess = () => {
		setOpenSuccessModal(true);
		onRetryTable();
		onClearSelectedEmployees();
		successModalTimeout.current && clearTimeout(successModalTimeout.current);

		successModalTimeout.current = setTimeout(() => {
			setOpenSuccessModal(false);
			onCloseReferFlow();
		}, timeOutSuccess);
	};

	return {
		isOpenSuccessModal,
		handleOpenModalSuccess,
		referedAmount,
		onSaveResults,
	};
};

export const useFirstAccess = () => {
	const { setSessionStorage, getSessionStorage } = useSessionStorage();

	const [isFirstAccess, setFirstAccess] = useState<boolean>(
		!getSessionStorage('psychologyAd'),
	);

	const handleCloseFirstAccess = () => {
		setFirstAccess(false);
		setSessionStorage('psychologyAd', 'true');
	};

	return {
		isFirstAccess,
		handleCloseFirstAccess,
	};
};

export const useMe = () => {
	const { getSessionStorage } = useSessionStorage();
	const sessionStorage: any = getSessionStorage('me');
	const company = JSON.parse(sessionStorage);

	return {
		company,
	};
};

export const useCancelConfirm = () => {
	const [isOpenCancelConfirm, setOpenCancelConfirm] = useState<boolean>(false);

	const handleOpenCancelConfirm = () => {
		setOpenCancelConfirm(true);
	};

	return {
		handleOpenCancelConfirm,
		isOpenCancelConfirm,
	};
};

type modalType =
	| ''
	| 'successModal'
	| 'firstAccess'
	| 'psycology'
	| 'ocupationalDoctor'
	| 'withoutDoctor'
	| 'cancelModal';

interface CurrentModalStateProps {
	isOpenSuccessModal: boolean;
	isFirstAccess: boolean;
	hasPsychology: boolean;
	isReferVisible: boolean;
	onCloseReferFlow: () => void;
}

export const useCurrentModalState = ({
	isOpenSuccessModal,
	isFirstAccess,
	hasPsychology,
	isReferVisible,
	onCloseReferFlow,
}: CurrentModalStateProps) => {
	const [currentState, setCurrentState] = useState<modalType>('');
	const [previewsStep, setPreviewsStep] = useState<modalType>('');
	const [hasNoDoctors, setNoDoctors] = useState<boolean>(false);

	const getCurrentStateKey = useCallback(() => {
		if (previewsStep) {
			return setCurrentState('cancelModal');
		}

		if (isOpenSuccessModal) {
			return setCurrentState('successModal');
		}

		if (isFirstAccess && !hasPsychology) {
			return setCurrentState('firstAccess');
		}

		if (hasPsychology) {
			return setCurrentState('psycology');
		}

		if (!hasPsychology && hasNoDoctors) {
			return setCurrentState('withoutDoctor');
		}

		if (!hasPsychology) {
			return setCurrentState('ocupationalDoctor');
		}

		return setCurrentState('');
	}, [
		hasNoDoctors,
		hasPsychology,
		isFirstAccess,
		isOpenSuccessModal,
		previewsStep,
	]);

	const handleGetPreviewsSteps = (stepName: modalType) => {
		setPreviewsStep(stepName);
	};

	const backToPreviewsStep = () => {
		setCurrentState(previewsStep);
		setPreviewsStep('');
	};

	const handleConfirmCancelRefer = () => {
		onCloseReferFlow();
		setPreviewsStep('');
	};

	const handleNoDoctors = () => setNoDoctors(true);
	const handleClearNoDoctors = () => setNoDoctors(false);

	useEffect(() => {
		if (isReferVisible) {
			getCurrentStateKey();
		}
	}, [getCurrentStateKey, isReferVisible]);

	return {
		currentState,
		handleNoDoctors,
		handleGetPreviewsSteps,
		backToPreviewsStep,
		handleConfirmCancelRefer,
		handleClearNoDoctors,
	};
};

interface SendReferHookProps {
	selectedEmployees: any;
	selectedCommunication: any;
	onSuccess: () => void;
	onSaveResults: (e: any) => void;
	onNoDoctors?: () => void;
	isAllEmployees: boolean;
}

export const useSendRefers = ({
	selectedEmployees,
	selectedCommunication,
	onSuccess,
	onSaveResults,
	onNoDoctors,
	isAllEmployees,
}: SendReferHookProps) => {
	const [isLoading, setLoading] = useState(false);
	const [hasError, setError] = useState(false);
	const { toast } = useToast();

	const campaignId: string = window.location.pathname.split('/').pop() || '';

	const sendRefers = async () => {
		setError(false);
		setLoading(true);
		const attributeName = isAllEmployees ? 'allEmployees' : 'employeeIds';

		const attributeValue =
			attributeName === 'employeeIds' ? selectedEmployees : true;

		const payload = {
			[attributeName]: attributeValue,
		};

		try {
			const { data } = await postReferEmployee(
				campaignId,
				selectedCommunication.id,
				payload,
			);
			Analytics.track('campaign', 'refer-employees', {
				selectedEmployees: selectedEmployees,
			});

			onSaveResults(data);
			onSuccess();
		} catch (error) {
			const { response } = error;

			if (
				response.status === 400 &&
				response.data.errors.some(
					(e: any) => e.message === 'Doctors not found',
				) &&
				onNoDoctors
			) {
				onNoDoctors();
				return;
			}
			setError(true);
			loggerInfo({
				data: error,
				context: 'campaign',
				location: 'handlePostRefer',
				payloadRequest: {
					campaignId,
					selectedCommunication: selectedCommunication.id,
					payload,
				},
			});
			toast.error(
				'Hubo un error al intentar procesar esta operación. Inténtalo de nuevo más tarde.',
			);
		} finally {
			setLoading(false);
		}
	};

	return {
		hasError,
		isLoading,
		sendRefers,
	};
};
